<template>
  <section class="devices">
    <div class="row mb-3">
      <div class="col-md-6">
        <h2>Registered Products</h2>
        <p>Product registrations and extended warranties.</p>
      </div>
      <div class="col-md-6 text-right">
        <div class="warranty-stats" v-if="warrantyStats.total > 0">
          <span class="stats-badge stats-total">Warranties: <strong>{{ warrantyStats.total }}</strong></span>
          <span class="stats-badge stats-generated">Pending: <strong>{{ warrantyStats.generated }}</strong></span>
          <span class="stats-badge stats-activated">Activated: <strong>{{ warrantyStats.activated }}</strong></span>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <ul class="nav nav-tabs mb-3">
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'devices' }" href="#" @click.prevent="activeTab = 'devices'">
          <i class="fa fa-mobile-alt mr-1"></i> Registered Products
          <span class="badge badge-secondary ml-1" v-if="devices_list.length">{{ devices_list.length }}</span>
        </a>
      </li>
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'warranties' }" href="#" @click.prevent="switchToWarranties">
          <i class="fa fa-shield-alt mr-1"></i> Warranties
          <span class="badge badge-secondary ml-1" v-if="warrantyStats.total">{{ warrantyStats.total }}</span>
        </a>
      </li>
    </ul>

    <!-- ==================== TAB: DEVICES ==================== -->
    <div v-show="activeTab === 'devices'">
      <!-- Filters -->
      <div class="row no-gutters filters-row mb-3">
        <div class="col-12 col-md-4 pr-md-2 mb-2">
          <label>Search:</label>
          <input
            v-model="search"
            @input="applyDeviceFilters"
            type="text"
            placeholder="Serial, name, email, order ID..."
            class="form-control form-control-sm"
          />
        </div>
        <div class="col-6 col-md-2 pr-md-2 mb-2">
          <label>Channel:</label>
          <select v-model="filterChannel" @change="applyDeviceFilters" class="form-control form-control-sm">
            <option value="">All Channels</option>
            <option v-for="ch in availableChannels" :key="ch" :value="ch">{{ ch }}</option>
          </select>
        </div>
        <div class="col-6 col-md-2 pr-md-2 mb-2">
          <label>Country:</label>
          <select v-model="filterCountry" @change="applyDeviceFilters" class="form-control form-control-sm">
            <option value="">All Countries</option>
            <option v-for="c in availableCountries" :key="c" :value="c">{{ c }}</option>
          </select>
        </div>
        <div class="col-6 col-md-2 pr-md-2 mb-2">
          <label>Warranty:</label>
          <select v-model="filterWarranty" @change="applyDeviceFilters" class="form-control form-control-sm">
            <option value="">All</option>
            <option value="extended">With Extended Warranty</option>
            <option value="standard">Standard Only</option>
          </select>
        </div>
        <div class="col-6 col-md-2 mb-2 d-flex align-items-end">
          <button class="btn btn-sm btn-outline-secondary" @click="resetDeviceFilters">Reset</button>
          <span class="ml-2 text-muted small align-self-center">{{ filteredDevices.length }} / {{ devices_list.length }}</span>
        </div>
      </div>

      <!-- Table -->
      <table class="table table-striped table-sm" v-if="filteredDevices.length">
        <thead>
          <tr>
            <th>Serial</th>
            <th>Type</th>
            <th>Owner</th>
            <th>Country</th>
            <th>Purchase</th>
            <th>Production</th>
            <th>Warranty</th>
            <th>Registered at</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="device in paginatedDevices" :key="device._id">
            <td>
              <div>
                <a v-if="device.crm_id" :href="`https://vitesy.lightning.force.com/lightning/r/TM_Device__c/${device.crm_id}/view`" target="_blank">
                  <strong>{{ device.serial }}</strong>
                </a>
                <strong v-else>{{ device.serial }}</strong>
                <br>
                <a v-if="device.mac" :href="`/production-test/${device.mac}`" class="text-muted small">{{ device.mac }}</a>
              </div>
            </td>
            <td>
              <div>{{ device.name }}</div>
              <div class="text-muted small">{{ device.sku }}</div>
            </td>
            <td>
              <div>
                <a v-if="device.owner && device.owner.crm_id" :href="`https://vitesy.lightning.force.com/lightning/r/Contact/${device.owner.crm_id}/view`" target="_blank">
                  {{ device.owner.first_name }} {{ device.owner.last_name }}
                </a>
                <span v-else-if="device.owner">{{ device.owner.first_name }} {{ device.owner.last_name }}</span>
              </div>
              <div v-if="device.owner" class="text-muted small">{{ device.owner.email }}</div>
              <div v-if="device.owner && device.owner.phone" class="text-muted small">{{ device.owner.phone }}</div>
            </td>
            <td>
              <span v-if="device.owner">{{ device.owner.country }}</span>
            </td>
            <td>
              <div v-if="device.purchase">
                <span :class="`channel-badge channel-${device.purchase.channel}`">
                  {{ device.purchase.channel }}
                  <template v-if="device.purchase.store"> / {{ device.purchase.store }}</template>
                </span>
                <div class="small mt-1"><strong>{{ device.purchase.order_id }}</strong></div>
              </div>
            </td>
            <td>
              <div v-if="device.production && device.production.id_number">
                <a :href="`/production/${device.production.id_number}/certificate`">{{ device.production.id_number }}</a>
                <div class="text-muted small">{{ device.production.timestamp | date }}</div>
              </div>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <div v-if="device.warranty_extension && device.warranty_extension.code">
                <span class="warranty-badge warranty-active">Extended</span>
                <div class="small mt-1">{{ device.warranty_extension.code }}</div>
                <div class="text-muted small">Exp: {{ device.warranty_extension.expires_at | date }}</div>
              </div>
              <span v-else class="warranty-badge warranty-standard">Standard</span>
            </td>
            <td>
              <span class="small">{{ device.createdAt | date }}</span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <nav v-if="deviceTotalPages > 1" class="d-flex justify-content-center">
        <ul class="pagination pagination-sm">
          <li class="page-item" :class="{ disabled: devicePage === 1 }">
            <a class="page-link" href="#" @click.prevent="devicePage = devicePage - 1">Prev</a>
          </li>
          <li v-for="page in devicePaginationRange" :key="page" class="page-item" :class="{ active: devicePage === page }">
            <a class="page-link" href="#" @click.prevent="devicePage = page">{{ page }}</a>
          </li>
          <li class="page-item" :class="{ disabled: devicePage === deviceTotalPages }">
            <a class="page-link" href="#" @click.prevent="devicePage = devicePage + 1">Next</a>
          </li>
        </ul>
      </nav>

      <!-- Loading / Empty -->
      <div v-if="!filteredDevices.length">
        <div class="loading-data" v-if="loadingDevices">
          <span class="loading"></span>
          <h5>Loading devices ...</h5>
        </div>
        <div class="loading-data" v-else-if="devices_list.length">
          <h6>No devices match your filters.</h6>
        </div>
        <div class="loading-data" v-else>
          <h6>No devices found.</h6>
        </div>
      </div>
    </div>

    <!-- ==================== TAB: WARRANTIES ==================== -->
    <div v-show="activeTab === 'warranties'">
      <!-- Filters -->
      <div class="row no-gutters filters-row mb-3">
        <div class="col-12 col-md-5 pr-md-2 mb-2">
          <label>Search:</label>
          <input
            v-model="warrantySearch"
            @keyup.enter="getWarranties"
            type="text"
            placeholder="Code, order ID, email, serial..."
            class="form-control form-control-sm"
          />
        </div>
        <div class="col-6 col-md-3 pr-md-2 mb-2">
          <label>Status:</label>
          <select v-model="warrantyFilterStatus" @change="getWarranties" class="form-control form-control-sm">
            <option value="">All</option>
            <option value="generated">Pending (not activated)</option>
            <option value="activated">Activated</option>
          </select>
        </div>
        <div class="col-6 col-md-4 mb-2 d-flex align-items-end">
          <button class="btn btn-sm btn-outline-secondary" @click="resetWarrantyFilters">Reset</button>
          <span class="ml-2 text-muted small align-self-center">{{ warranties.length }} results</span>
        </div>
      </div>

      <!-- Table -->
      <table class="table table-striped table-sm" v-if="warranties.length">
        <thead>
          <tr>
            <th>Code</th>
            <th>Status</th>
            <th>Order</th>
            <th>Product</th>
            <th>Purchaser</th>
            <th>Purchased</th>
            <th>Activated by</th>
            <th>Device</th>
            <th>Warranty Expires</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="w in paginatedWarranties" :key="w._id">
            <td>
              <strong class="text-monospace">{{ w.code || w._id }}</strong>
            </td>
            <td>
              <span class="warranty-badge" :class="w.status === 'activated' ? 'warranty-active' : 'warranty-pending'">
                {{ w.status === 'activated' ? 'Activated' : 'Pending' }}
              </span>
            </td>
            <td>
              <a :href="`/orders/${w.order_id}`">{{ w.order_id }}</a>
            </td>
            <td>
              <div>{{ w.product_name }}</div>
              <div class="text-muted small">{{ w.product_sku }}</div>
            </td>
            <td>
              <div v-if="w.purchaser">
                {{ w.purchaser.first_name }} {{ w.purchaser.last_name }}
                <div class="text-muted small">{{ w.purchaser.email }}</div>
              </div>
            </td>
            <td>
              <span class="small">{{ w.purchase_date | date }}</span>
            </td>
            <td>
              <div v-if="w.activation && w.activation.registered_by && w.activation.registered_by.email">
                {{ w.activation.registered_by.first_name }} {{ w.activation.registered_by.last_name }}
                <div class="text-muted small">{{ w.activation.registered_by.email }}</div>
                <div class="text-muted small">{{ w.activation.date | date }}</div>
              </div>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <span v-if="w.activation && w.activation.device_serial">
                <strong>{{ w.activation.device_serial }}</strong>
              </span>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <div v-if="w.extended_warranty_end_date">
                <div class="small">{{ w.extended_warranty_end_date | date }}</div>
                <div class="text-muted small">(std: {{ w.standard_warranty_end_date | date }})</div>
              </div>
              <span v-else class="text-muted">-</span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <nav v-if="warrantyTotalPages > 1" class="d-flex justify-content-center">
        <ul class="pagination pagination-sm">
          <li class="page-item" :class="{ disabled: warrantyPage === 1 }">
            <a class="page-link" href="#" @click.prevent="warrantyPage = warrantyPage - 1">Prev</a>
          </li>
          <li v-for="page in warrantyPaginationRange" :key="page" class="page-item" :class="{ active: warrantyPage === page }">
            <a class="page-link" href="#" @click.prevent="warrantyPage = page">{{ page }}</a>
          </li>
          <li class="page-item" :class="{ disabled: warrantyPage === warrantyTotalPages }">
            <a class="page-link" href="#" @click.prevent="warrantyPage = warrantyPage + 1">Next</a>
          </li>
        </ul>
      </nav>

      <!-- Loading / Empty -->
      <div v-if="!warranties.length">
        <div class="loading-data" v-if="loadingWarranties">
          <span class="loading"></span>
          <h5>Loading warranties ...</h5>
        </div>
        <div class="loading-data" v-else>
          <h6>No warranties found.</h6>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import DevicesService from "@/services/DevicesService";
import WarrantyService from "@/services/WarrantyService";

export default {
  name: "Devices",
  data() {
    return {
      activeTab: "devices",

      // Devices
      loadingDevices: false,
      devices_list: [],
      filteredDevices: [],
      search: "",
      filterChannel: "",
      filterCountry: "",
      filterWarranty: "",
      devicePage: 1,
      perPage: 50,

      // Warranties
      loadingWarranties: false,
      warranties: [],
      warrantySearch: "",
      warrantyFilterStatus: "",
      warrantyPage: 1,
      warrantyStats: { total: 0, generated: 0, activated: 0 },
      warrantiesLoaded: false,
    };
  },
  computed: {
    // Devices pagination
    deviceTotalPages() {
      return Math.ceil(this.filteredDevices.length / this.perPage);
    },
    paginatedDevices() {
      const start = (this.devicePage - 1) * this.perPage;
      return this.filteredDevices.slice(start, start + this.perPage);
    },
    devicePaginationRange() {
      return this.calcPaginationRange(this.devicePage, this.deviceTotalPages);
    },
    // Warranties pagination
    warrantyTotalPages() {
      return Math.ceil(this.warranties.length / this.perPage);
    },
    paginatedWarranties() {
      const start = (this.warrantyPage - 1) * this.perPage;
      return this.warranties.slice(start, start + this.perPage);
    },
    warrantyPaginationRange() {
      return this.calcPaginationRange(this.warrantyPage, this.warrantyTotalPages);
    },
    // Dynamic filters
    availableChannels() {
      const channels = new Set();
      this.devices_list.forEach(d => {
        if (d.purchase && d.purchase.channel) channels.add(d.purchase.channel);
      });
      return Array.from(channels).sort();
    },
    availableCountries() {
      const countries = new Set();
      this.devices_list.forEach(d => {
        if (d.owner && d.owner.country) countries.add(d.owner.country);
      });
      return Array.from(countries).sort();
    },
  },
  mounted() {
    this.getDevices();
    this.getWarrantyStats();
  },
  methods: {
    calcPaginationRange(current, total) {
      const range = [];
      const maxVisible = 7;
      let start = Math.max(1, current - Math.floor(maxVisible / 2));
      let end = Math.min(total, start + maxVisible - 1);
      if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
      for (let i = start; i <= end; i++) range.push(i);
      return range;
    },

    // ---- Devices ----
    async getDevices() {
      this.loadingDevices = true;
      try {
        const response = await DevicesService.getDevices();
        this.devices_list = response.data;
        this.applyDeviceFilters();
      } catch (err) {
        console.error(err);
      }
      this.loadingDevices = false;
    },
    applyDeviceFilters() {
      this.devicePage = 1;
      let result = this.devices_list;

      if (this.search) {
        const s = this.search.toLowerCase();
        result = result.filter(d => {
          const serial = (d.serial || d._id || '').toLowerCase();
          const mac = (d.mac || '').toLowerCase();
          const name = d.owner ? `${d.owner.first_name || ''} ${d.owner.last_name || ''}`.toLowerCase() : '';
          const email = d.owner ? (d.owner.email || '').toLowerCase() : '';
          const orderId = d.purchase ? (d.purchase.order_id || '').toLowerCase() : '';
          const sku = (d.sku || '').toLowerCase();
          const warrantyCode = d.warranty_extension ? (d.warranty_extension.code || '').toLowerCase() : '';
          return serial.includes(s) || mac.includes(s) || name.includes(s) || email.includes(s) || orderId.includes(s) || sku.includes(s) || warrantyCode.includes(s);
        });
      }
      if (this.filterChannel) {
        result = result.filter(d => d.purchase && d.purchase.channel === this.filterChannel);
      }
      if (this.filterCountry) {
        result = result.filter(d => d.owner && d.owner.country === this.filterCountry);
      }
      if (this.filterWarranty === 'extended') {
        result = result.filter(d => d.warranty_extension && d.warranty_extension.code);
      } else if (this.filterWarranty === 'standard') {
        result = result.filter(d => !d.warranty_extension || !d.warranty_extension.code);
      }

      this.filteredDevices = result;
    },
    resetDeviceFilters() {
      this.search = "";
      this.filterChannel = "";
      this.filterCountry = "";
      this.filterWarranty = "";
      this.applyDeviceFilters();
    },

    // ---- Warranties ----
    switchToWarranties() {
      this.activeTab = 'warranties';
      if (!this.warrantiesLoaded) {
        this.getWarranties();
      }
    },
    async getWarranties() {
      this.loadingWarranties = true;
      this.warrantyPage = 1;
      try {
        const params = {};
        if (this.warrantySearch) params.search = this.warrantySearch;
        if (this.warrantyFilterStatus) params.status = this.warrantyFilterStatus;
        const response = await WarrantyService.getWarranties(params);
        this.warranties = response.data;
        this.warrantiesLoaded = true;
      } catch (err) {
        console.error(err);
      }
      this.loadingWarranties = false;
    },
    async getWarrantyStats() {
      try {
        const response = await WarrantyService.getStats();
        this.warrantyStats = response.data;
      } catch (err) {
        console.error(err);
      }
    },
    resetWarrantyFilters() {
      this.warrantySearch = "";
      this.warrantyFilterStatus = "";
      this.getWarranties();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/variables.scss";

.nav-tabs {
  border-bottom: 2px solid #dee2e6;
  .nav-link {
    font-size: .85rem;
    font-weight: 600;
    color: #666;
    border: none;
    padding: .6rem 1.2rem;
    &.active {
      color: $vitesy;
      border-bottom: 2px solid $vitesy;
      background: transparent;
    }
    &:hover:not(.active) {
      color: #333;
      border-bottom: 2px solid #ccc;
    }
    .badge { font-size: .65rem; }
  }
}

.filters-row {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: .5rem;
  border: 1px solid #eee;
  label {
    font-size: .75rem;
    font-weight: 600;
    color: #666;
    margin-bottom: .25rem;
  }
}

.warranty-stats {
  .stats-badge {
    display: inline-block;
    padding: .25rem .6rem;
    border-radius: .3rem;
    font-size: .75rem;
    margin: .15rem;
    &.stats-total { background: #e8e8e8; }
    &.stats-generated { background: #fff3cd; color: #856404; }
    &.stats-activated { background: #d4edda; color: #155724; }
  }
}

.channel-badge {
  padding: .15rem .4rem;
  border-radius: .3rem;
  font-size: .7rem;
  display: inline-block;
  &.channel-Ecommerce { background-color: #d4edda; color: #155724; }
  &.channel-Amazon { background-color: #fff3cd; color: #856404; }
}

.warranty-badge {
  padding: .15rem .5rem;
  border-radius: .3rem;
  font-size: .7rem;
  font-weight: 600;
  display: inline-block;
  &.warranty-active { background-color: #d4edda; color: #155724; }
  &.warranty-standard { background-color: #f0f0f0; color: #666; }
  &.warranty-pending { background-color: #fff3cd; color: #856404; }
}

.loading-data {
  padding: 3rem;
  text-align: center;
  h5, h6 { margin-top: 1rem; }
}

table.table {
  font-size: .8rem;
  th {
    font-size: .7rem;
    text-transform: uppercase;
    color: #888;
    border-top: none;
  }
}

.text-monospace {
  font-family: 'Courier New', monospace;
  letter-spacing: .5px;
}
</style>
